import { chatStateAtom } from '@/atoms/chatAtoms';
import {
  IS_EVIDENCE_SBS_ENABLED,
  IS_INSET_DESIGN_ENABLED,
} from '@/features/flags/legacy-flags';
import { GET_CHAT_BY_ID } from '@/hooks/query/useQueryChatById';
import { cn } from '@/lib/cn';
import { logger } from '@/utils/logger/logger';
import { useLatestRef } from '@heidi/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  evidenceSBSChatIdAtom,
  evidenceSBSDisplayedTimeAtom,
  resetEvidenceSBSAction,
  shouldDoEvidenceSBSAtom,
} from '../atoms';
import { useEvidenceSBSStreams } from '../hooks/useEvidenceSBSStreams';
import { useSkipEvidenceSBSMutation } from '../mutations/useSkipEvidenceSBSMutation';
import { EvidenceSBSStreamStatus } from '../types';
import { EvidenceSBSComparison } from './EvidenceSBSComparison';

interface Props {
  chatId: string | null | undefined;
  /**
   * EVI-1680 (in-session polish): true when this overlay is mounted inside a
   * patient session (sidebar) rather than the full-page sessionless Evidence
   * chat. In-session the panel is much narrower and uses a different surface
   * token, so we (a) match the overlay/skip background to the in-session
   * container and (b) collapse the side-by-side comparison into a single-card
   * view with an Option toggle. Out-of-session behaviour is unchanged.
   */
  inSession?: boolean;
}

export function EvidenceSBSInline({ chatId, inSession = false }: Props) {
  if (!IS_EVIDENCE_SBS_ENABLED()) return null;
  return <EvidenceSBSInlineInner chatId={chatId} inSession={inSession} />;
}

function EvidenceSBSInlineInner({ chatId, inSession = false }: Props) {
  const hasCandidates = useAtomValue(shouldDoEvidenceSBSAtom);
  const sbsChatId = useAtomValue(evidenceSBSChatIdAtom);
  const displayedTime = useAtomValue(evidenceSBSDisplayedTimeAtom);
  const { streamA, streamB, candidates } = useEvidenceSBSStreams(chatId);
  const resetSBS = useSetAtom(resetEvidenceSBSAction);
  const setChatState = useSetAtom(chatStateAtom);
  const queryClient = useQueryClient();
  const { mutate: skipMutate } = useSkipEvidenceSBSMutation();
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const skippedRef = useRef(false);
  // Idempotency guard for the skip-flow effect — its deps can settle
  // through several intermediate states before reaching `!isStreaming`,
  // and we want sync + dismiss to fire exactly once per SBS round.
  const skipHandledRef = useRef(false);

  useEffect(() => {
    if (hasCandidates) {
      setDismissed(false);
      setConfirming(false);
      setSkipped(false);
      skippedRef.current = false;
      skipHandledRef.current = false;
    }
  }, [hasCandidates]);

  // EVI-1680 (bug B): when this overlay is mounted under a chat that differs from
  // the experiment's chat, the atoms hold an experiment from a different chat.
  // We must NOT `resetSBS()` here — that aborts the in-flight streams, and the
  // mounted chat id is frequently just a transient/unresolved value while the
  // panel remounts on a tab switch (sessionless / eager-created Evidence chat),
  // which would cancel the answer the user is waiting on. Instead we simply hide
  // the overlay (see the `sbsChatId && sbsChatId !== chatId` render guard below)
  // and let `shouldBlockStreamStart` in `useEvidenceSBSStreams` keep a genuinely
  // leaked experiment from starting fresh streams. The experiment is cleared on
  // the next send via `resetEvidenceSBSAction`.

  /**
   * Stream→chat handoff for the chosen candidate. Has to handle two cases
   * differently because `useChatConversation`'s sync effect bails out when
   * `streamingState !== null` — so anything we leave on chatStateAtom that
   * fakes "still streaming" will permanently block the backend refetch from
   * landing the real response.
   *
   * CASE A — chosen stream already has real chunks: copy them in with a
   *   fresh `lastActivity` so legacyTimelineBuilder's grace-period rule
   *   preserves them during DB replica replication.
   * CASE B — early click, no real chunks yet: drop everything, write
   *   `[userMessage]` with `streamingState: null`, bump `lastActivity` to
   *   now. Backend refetch will replace `[userMessage]` with the canonical
   *   `[userMessage, response]` within 1-3s. The brief "user question only,
   *   no loader" window is the same as the original chat-send flow's
   *   pre-stream gap. `recentlySBSConfirmedAt` suppresses the
   *   "Reconnecting…" indicator during this window.
   *
   * Refs (not direct values) because confirmMutate's onSuccess fires 1-3s
   * after this callback was captured — without refs the sync sees the
   * stale at-click snapshot, missing chunks that arrived during the
   * mutation roundtrip.
   */
  const candidatesRef = useLatestRef(candidates);
  const streamARef = useLatestRef(streamA);
  const streamBRef = useLatestRef(streamB);

  const syncChosenStreamToChat = useCallback(
    (chosenCandidateId: string) => {
      if (!chatId) return;
      const stateKey = `chat-${chatId}`;
      const latestCandidates = candidatesRef.current;
      const chosenIndex = latestCandidates.findIndex(
        (c) => c.candidate_id === chosenCandidateId
      );
      if (chosenIndex === -1) return;
      const chosenStream = chosenIndex === 0 ? streamARef.current : streamBRef.current;
      const streamedTimeline = chosenStream?.conversation.timeline ?? [];

      // TimelineEntry is a discriminated union; UserMessageEntry has no
      // `backendIndex`. Narrow with `'in'` so we don't lie about the shape.
      const isPlaceholder = (entry: unknown): boolean =>
        typeof entry === 'object' &&
        entry !== null &&
        'backendIndex' in entry &&
        (entry as { backendIndex?: number }).backendIndex === -1;
      const realStreamed = streamedTimeline.filter((e) => !isPlaceholder(e));
      const hasRealStreamed = realStreamed.length > 0;

      const now = Date.now();
      setChatState((prev) => {
        const existing = prev[stateKey];
        const existingTimeline = existing?.timeline ?? [];
        const userOnly = existingTimeline.filter((e) => e.type === 'user_message');
        const newTimeline = hasRealStreamed
          ? [...existingTimeline.filter((e) => !isPlaceholder(e)), ...realStreamed]
          : userOnly;
        return {
          ...prev,
          [stateKey]: {
            timeline: newTimeline,
            streamingState: null,
            metadata: {
              ...existing?.metadata,
              lastActivity: now,
              clientChatId: chatId,
              recentlySBSConfirmedAt: now,
            },
          },
        };
      });

      if (!hasRealStreamed) {
        logger.warn(
          '[EVIDENCE_SBS] chosen stream had no real content yet — committing user message only; backend refetch will fill in the response',
          { chosenCandidateId, chosenIndex }
        );
      }
    },
    [chatId, setChatState, candidatesRef, streamARef, streamBRef]
  );

  /**
   * Pre-EVI-1403 this path eagerly deleted `chat-${chatId}` from
   * chatStateAtom on dismiss, leaving recovery to think the chat had no
   * data and triggering a "Reconnecting…" indicator the user couldn't
   * escape from until refresh. Now: keep whatever's in local state, just
   * invalidate the chat query so the canonical state takes over when the
   * refetch returns.
   */
  const dismissAndRefetch = useCallback(() => {
    setDismissed(true);
    resetSBS();
    void queryClient.invalidateQueries({ queryKey: [GET_CHAT_BY_ID, chatId] });
  }, [resetSBS, queryClient, chatId]);

  const handleConfirmed = useCallback(() => {
    setConfirming(true);
  }, []);

  const handleMutationSuccess = useCallback(
    (chosenCandidateId?: string) => {
      // `undefined` arrives via the "both streams failed" auto-dismiss
      // path — there's nothing to sync.
      if (chosenCandidateId) syncChosenStreamToChat(chosenCandidateId);
      setDismissed(true);
      setConfirming(false);
      resetSBS();
      void queryClient.invalidateQueries({ queryKey: [GET_CHAT_BY_ID, chatId] });
    },
    [queryClient, chatId, resetSBS, syncChosenStreamToChat]
  );

  const handleSkip = useCallback(() => {
    setSkipped(true);
    skippedRef.current = true;
  }, []);

  const defaultStream = candidates[0]?.is_default ? streamA : streamB;
  const defaultCandidate = candidates.find((c) => c.is_default) ?? candidates[0];

  /**
   * Skip-flow dismiss: the default candidate's response is already
   * persisted by the streaming completion (`skip_chat_save=False` for
   * non-alt candidates in `manager/chat/chat_manager.py`), so the chat
   * already contains the assistant message by the time the user clicks
   * Skip. We deliberately do NOT call `/chat/sbs/confirm` here —
   * doing so would mark `is_user_selected = TRUE, selected_alternative =
   * FALSE` in `evidence_sbs_experiment_result` and contaminate the SBS
   * win-rate measurement (every Skip would look like the user explicitly
   * picked the default model).
   *
   * Wait for the default stream to settle so `syncChosenStreamToChat`
   * has the full content to copy into `chatStateAtom`; that bridges the
   * 1-3s refetch window during which the backend's MongoDB replica may
   * not yet reflect the just-persisted message.
   *
   * PostHog still records `evidence_sbs_action: 'selection_skipped'`
   * from `EvidenceSBSComparison.handleSkip` for analytics visibility.
   */
  useEffect(() => {
    if (
      !skippedRef.current ||
      !defaultStream ||
      defaultStream.isStreaming ||
      // `!isStreaming` is also true in `WAITING_START` (pre-first-chunk),
      // not just at terminal `DONE`/`ERROR`. Without this guard, a Skip
      // click in the brief window between candidates loading and the
      // first SSE chunk would fire sync+dismiss immediately, which
      // aborts both streams via `resetSBS` (see `useEvidenceSBSStreams`'s
      // candidates-empty effect) — the backend's `_save_chat_completion`
      // never runs and the chat is left with only the user message
      // permanently. Hold the skip in `armed` state until the stream
      // actually starts and finishes.
      defaultStream.status === EvidenceSBSStreamStatus.WAITING_START ||
      !defaultCandidate ||
      !chatId ||
      skipHandledRef.current
    ) {
      return;
    }
    skipHandledRef.current = true;

    // Fire-and-forget: tell the backend the user dismissed so it can persist
    // the Redis cooldown (`evidence_sbs_limit:{user_id}`) and stop
    // re-triggering SBS on the next message. We deliberately do not await or
    // gate dismiss UX on this — the overlay should disappear immediately for
    // the user; a transient skip API failure just means SBS may show again
    // next time, which is a strictly better failure mode than blocking the
    // dismiss. The endpoint does NOT call confirm semantics so win-rate
    // metrics stay clean (`is_user_selected` remains false on the
    // experiment row).
    const nowIso = new Date().toISOString();
    skipMutate({
      reqBody: {
        chat_id: chatId,
        candidate_id: defaultCandidate.candidate_id,
        displayed_at: displayedTime?.toISOString() ?? null,
        skipped_at: nowIso,
      },
    });

    syncChosenStreamToChat(defaultCandidate.candidate_id);
    dismissAndRefetch();
  }, [
    skipped,
    defaultStream,
    defaultStream?.isStreaming,
    defaultStream?.status,
    defaultCandidate,
    chatId,
    displayedTime,
    skipMutate,
    dismissAndRefetch,
    syncChosenStreamToChat,
  ]);

  if (!chatId) return null;
  if (dismissed) return null;
  if (sbsChatId && sbsChatId !== chatId) return null;
  if (!hasCandidates && !confirming && !skipped) {
    return null;
  }

  // Skip-path minimal view: collapse Comparison to the default candidate's
  // card without remounting its React subtree so streaming content keeps
  // the same DOM identity while the default stream finishes.
  const minimalCandidateId =
    skipped && defaultCandidate ? defaultCandidate.candidate_id : null;

  // EVI-1680 (in-session polish): match the overlay's background plane to the
  // chat pane it sits over. The pane uses `bg-surface-container` in-session
  // (non-inset) but `bg-surface` everywhere else (see Chat.tsx), so the
  // previously-hardcoded `bg-surface` overlay produced a visible seam
  // in-session. Out-of-session and inset-design resolve to `bg-surface`,
  // exactly as before. `top-20` (not `inset-0`) on the wrapper below leaves
  // the chat header visible so the user keeps their question/title context.
  const surfaceBgClass =
    inSession && !IS_INSET_DESIGN_ENABLED() ? 'bg-surface-container' : 'bg-surface';

  return (
    <div
      className={cn(
        'absolute inset-x-0 bottom-0 top-20 z-10 overflow-y-auto',
        surfaceBgClass
      )}
    >
      {/* EVI-1602: drop the top padding so the heading sits close to the
          query title that shows through above the overlay, and trim the
          bottom padding so the sticky Skip strip sits close to the chat
          input instead of floating well above it. */}
      <div className="mx-auto flex size-full max-w-8xl flex-col px-4 pb-2 pt-0">
        <EvidenceSBSComparison
          chatId={chatId}
          candidates={candidates}
          streamA={streamA}
          streamB={streamB}
          onConfirmed={handleConfirmed}
          onMutationSuccess={handleMutationSuccess}
          onSkip={handleSkip}
          minimalCandidateId={minimalCandidateId}
          inSession={inSession}
          surfaceClassName={surfaceBgClass}
        />
      </div>
    </div>
  );
}
